import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, UserCog, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getSupervisorsPayslip } from '@/services/payrollApi';

const SupervisorPayslip = ({ dateFrom, dateTo, money, qtyFmt }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getSupervisorsPayslip({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }));
    } catch (e) { toast.error('Failed to load supervisor payslip'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.supervisors || [];

  return (
    <Card data-testid="supervisor-payslip">
      <CardHeader className="print:hidden">
        <CardTitle className="flex items-center gap-2"><UserCog className="w-5 h-5" />Supervisor Wages</CardTitle>
        <CardDescription>
          Base wage = output produced during the shifts the supervisor was present for × rate (night
          pieces carry the night-shift uplift). Incentive is earned only on days he was present. Mark
          day/night attendance in the grid above — unmarked days are treated as absent.
          {data && <span className="ml-2 font-medium text-[#1A1F2E]">Grand total: {money(data.grand_total?.total)}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-[#5A5F6E]" data-testid="supervisor-payslip-empty">
            No supervisors assigned yet. Assign supervisors in the Settings tab.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => (
              <div key={`${r.operation_code}-${r.supervisor_staff_id}`} className="border rounded-lg overflow-hidden" data-testid={`supervisor-slip-${r.operation_code}-${r.supervisor_staff_id}`}>
                <div className="bg-[#F5F1E8] px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-[#1A1F2E]">{r.operation} <span className="text-xs font-normal text-[#5A5F6E]">· {r.module}</span></div>
                    <div className="text-sm text-[#2D5F4F]">{r.supervisor_staff_name} <span className="text-[#5A5F6E]">({r.supervisor_staff_id})</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#1A1F2E]" data-testid={`supervisor-total-${r.operation_code}-${r.supervisor_staff_id}`}>{money(r.total_amount)}</div>
                    <div className="text-xs text-[#5A5F6E]" data-testid={`supervisor-credited-${r.operation_code}-${r.supervisor_staff_id}`}>{qtyFmt(r.credited_qty)} pcs credited · {r.days_worked} day(s) present</div>
                  </div>
                </div>
                <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-[#5A5F6E] uppercase mb-1">Base wage</div>
                    <div className="text-sm font-semibold">{money(r.base_wage)}</div>
                    <div className="text-xs text-[#5A5F6E] mt-1">
                      Day: {qtyFmt(r.day_qty)} pcs → {money(r.base_wage_day)} · Night: {qtyFmt(r.night_qty)} pcs → {money(r.base_wage_night)} <span className="italic">(incl. uplift)</span>
                    </div>
                    <div className="text-xs text-[#5A5F6E] mt-0.5">Rate ₹{r.rate}/pc · Target/staff/day: {qtyFmt(r.target_qty) || '—'} · Daily incentive pool: {money(r.daily_incentive)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[#5A5F6E] uppercase mb-1">Incentive: {money(r.total_incentive)}</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {(r.criteria || []).map((c) => (
                          <tr key={c.criterion_id} className="border-b last:border-0" data-testid={`supervisor-crit-${r.operation_code}-${r.supervisor_staff_id}-${c.criterion_id}`}>
                            <td className="py-1">{c.name}</td>
                            <td className="py-1 text-center text-[#5A5F6E]">{c.weightage_pct}%</td>
                            <td className="py-1 text-center">
                              {c.status === 'pending' ? (
                                <Badge variant="secondary" className="text-[10px] flex items-center gap-1 w-fit mx-auto"><Info className="w-3 h-3" />data pending</Badge>
                              ) : c.status === 'na' ? (
                                <span className="text-[#B45309]">no target</span>
                              ) : (
                                <span className="text-[#2D5F4F]">{c.avg_met_pct}% met</span>
                              )}
                            </td>
                            <td className="py-1 text-right font-medium">{money(c.incentive)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupervisorPayslip;
